import React, { useRef, useEffect, useState } from 'react';
import './styles/TypingAreaBlock.css';

const TypingAreaBlock = ({ sentence, cursorPosition, attempt, previousAttemptWPM, runActive, colors }) => {
    const cursorRef = useRef(null);
    const containerRef = useRef(null);
    const ghostCursorRafRef = useRef(null);
    const ghostCursorStartTsRef = useRef(null);
    const [displayedLineIndex, setDisplayedLineIndex] = useState(0);
    const [ghostCursorPosition, setGhostCursorPosition] = useState(0);
    const charsPerLine = Math.min(Math.floor(window.innerWidth / 15), 75);
    const ghostCharsPerSec = (previousAttemptWPM * 5) / 60;
    const isGhostCursorActive = previousAttemptWPM > 0 && runActive;


    const splitSentenceIntoChunks = (text, chunkSize) => {
        const chunks = [];
        let index = 0;
        while (index < text.length) {
            let endIndex = index + chunkSize;
            if (endIndex < text.length) {
                while (text[endIndex] !== ' ' && endIndex > index) {
                    endIndex--;
                }
                if (endIndex !== index) {
                    endIndex++;
                }
            }
            const chunk = text.slice(index, endIndex).trim();
            chunks.push(chunk);
            index = endIndex;
        }
        for (let i = 0; i < chunks.length - 1; i++) {
            chunks[i] = chunks[i] + " ";
        }
        return chunks;
    };  

    const sentenceChunks = splitSentenceIntoChunks(sentence, charsPerLine);
    const displayedChunks = sentenceChunks.slice(displayedLineIndex, displayedLineIndex + 3);

    const sentenceWithCursorAndColors = displayedChunks.map((chunk, chunkIndex) => (
        <div key={chunkIndex}>
          {chunk.split('').map((char, charIndex) => {
                const charsInPreviousChunks = sentenceChunks
                    .slice(0, displayedLineIndex + chunkIndex)
                    .reduce((sum, chunk) => sum + chunk.length, 0);
                const absoluteIndex = charsInPreviousChunks + charIndex;
                const isCursor = absoluteIndex === cursorPosition;
                const isSecondCursor = absoluteIndex === ghostCursorPosition;
                const isSpace = char === ' ';
                const isTyped = absoluteIndex < attempt.length;
                const isCorrect = isTyped && attempt[absoluteIndex] === char;
                const charClass = isTyped
                    ? isCorrect
                        ? isSpace
                            ? 'space-placeholder'
                            : 'correct'
                        : isSpace
                        ? 'space-wrong'
                        : 'incorrect'
                    : isCursor ? '' : 'not-typed'
                // get the color (if its cursor = highlight, if its not typed = undefined, if its correct = correct color, if its incorrect = incorrect color)
                const color = isTyped ? (isCorrect ? colors.correct : colors.incorrect) : isCursor ? colors.highlight : undefined; 
                return (
                    <span
                        key={charIndex}
                        ref={isCursor ? cursorRef : null}
                        className={`${charClass} ${isCursor ? 'cursor' : ''} ${isSecondCursor ? 'second-cursor' : ''}`}
                        style={{ color: color }}
                    >
                        {char}
                    </span>
                );
            })}
        </div>
    ));

    useEffect(() => {
        const checkAndUpdateDisplayedLines = () => {
            if (cursorRef.current && containerRef.current) {
                const lineIndex = Math.floor(cursorPosition / charsPerLine);
                if (lineIndex > displayedLineIndex + 1) {
                    setDisplayedLineIndex(lineIndex - 1);
                }
            }
        };

        checkAndUpdateDisplayedLines();
    }, [cursorPosition, attempt, charsPerLine, displayedLineIndex]);

    useEffect(() => {
        const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(() => callback(performance.now()), 16));
        const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;

        if (!isGhostCursorActive) {
            if (ghostCursorRafRef.current !== null) {
                cancelFrame(ghostCursorRafRef.current);
                ghostCursorRafRef.current = null;
            }
            ghostCursorStartTsRef.current = null;
            setGhostCursorPosition(0);
            return undefined;
        }

        const maxGhostPosition = sentence.length;
        const updateGhostCursor = (timestamp) => {
            if (ghostCursorStartTsRef.current === null) {
                ghostCursorStartTsRef.current = timestamp;
            }

            const elapsedSeconds = (timestamp - ghostCursorStartTsRef.current) / 1000;
            const nextPosition = Math.max(0, Math.min(maxGhostPosition, Math.floor(ghostCharsPerSec * elapsedSeconds)));
            setGhostCursorPosition((prevPosition) => (prevPosition === nextPosition ? prevPosition : nextPosition));

            if (nextPosition >= maxGhostPosition) {
                ghostCursorRafRef.current = null;
                return;
            }

            ghostCursorRafRef.current = requestFrame(updateGhostCursor);
        };

        ghostCursorRafRef.current = requestFrame(updateGhostCursor);

        return () => {
            if (ghostCursorRafRef.current !== null) {
                cancelFrame(ghostCursorRafRef.current);
                ghostCursorRafRef.current = null;
            }
            ghostCursorStartTsRef.current = null;
        };
    }, [ghostCharsPerSec, isGhostCursorActive, sentence]);

    return (
        <div className="typing-area-block-container" ref={containerRef}>
            <div className="sentence-block-container">
                <div className="typing-area-block">{sentenceWithCursorAndColors}</div>
            </div>
        </div>
    );
};

export default TypingAreaBlock;
